import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { niceStep, smoothProfile } from '../utils/telemetry';
import EventSearchSelect from './EventSearchSelect';
import RouvyAnalyzerModal from './RouvyAnalyzerModal';
import './RaceAnalysisModal.css';

// Разбор гонки на этапе: где участники теряли и выигрывали время.
// Данные готовит RaceAnalysisService:
// GET /api/events/:event_id/results/analysis/:route_id — участки маршрута,
// сплиты по ним и время прохождения контрольных точек дистанции.
const RIDER_COLORS = [
  '#667eea', '#e0625a', '#2fa27f', '#d99e2b', '#8b5cf6',
  '#0ea5e9', '#ec4899', '#65a30d', '#f97316', '#0f766e'
];

// Сколько участников показываем на графике отрывов по умолчанию
const DEFAULT_RIDERS = 5;

// Больше линий на графике не различить, да и цвета в палитре кончаются.
// На этапе может быть и сотня участников, поэтому выбор идет через список
// с поиском, а на графике одновременно живет не больше этого числа линий
const MAX_SHOWN_RIDERS = RIDER_COLORS.length;

const KIND_LABEL_KEYS = {
  climb: 'results.analysis_kind_climb',
  descent: 'results.analysis_kind_descent',
  flat: 'results.analysis_kind_flat'
};

// Категории подъемов в порядке убывания сложности — тем же порядком идет
// легенда под профилем. Категорию считает бэкенд (RouteSegmentsService)
const CATEGORIES = ['HC', '1', '2', '3', '4'];

// Класс для подложки на профиле и для плашки категории: цвет говорит,
// насколько тяжелый подъем
const categoryClass = (category) => (
  category ? `race-analysis-cat-${String(category).toLowerCase()}` : 'race-analysis-cat-none'
);

const pad = (value) => String(value).padStart(2, '0');

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';

  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

// Отставание: до минуты показываем десятые доли, дальше — минуты и секунды
const formatGap = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  if (Math.abs(seconds) < 0.05) return '—';

  const sign = seconds > 0 ? '+' : '−';
  const value = Math.abs(seconds);

  return `${sign}${value < 60 ? value.toFixed(1) : formatDuration(value)}`;
};

const RaceAnalysisModal = ({ isOpen, onClose, eventId, routeId, routeName, currentUserId }) => {
  const { t, language } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [tab, setTab] = useState('segments');
  // Выбранный участок маршрута — по нему строится таблица сплитов
  const [segmentIndex, setSegmentIndex] = useState(0);
  // Участник, относительно которого считаются отрывы (по умолчанию победитель)
  const [referenceId, setReferenceId] = useState(null);
  // Участники, чьи линии показаны на графике отрывов
  const [shownIds, setShownIds] = useState(() => new Set());
  // Поиск по списку участников
  const [riderQuery, setRiderQuery] = useState('');
  const [isAnalyzerOpen, setIsAnalyzerOpen] = useState(false);
  const [cursorDistance, setCursorDistance] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  const gapSvgRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isOpen || !eventId || !routeId) return undefined;

    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setTab('segments');
      setSegmentIndex(0);
      setReferenceId(null);
      setShownIds(new Set());
      setRiderQuery('');
      setIsAnalyzerOpen(false);
      setCursorDistance(null);

      try {
        const response = await axios.get(`/api/events/${eventId}/results/analysis/${routeId}`);
        if (!cancelled) setData(response.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || t('results.analysis_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      setLoading(false);
    };
    // t намеренно не в зависимостях: функция перевода пересоздается на каждом
    // рендере провайдера и сбрасывала бы уже загруженные данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId, routeId, reloadToken]);

  const segments = useMemo(() => (data && data.segments) || [], [data]);
  const checkpoints = useMemo(() => (data && data.checkpoints) || [], [data]);

  // Участники в порядке протокола: сначала с местом, потом сошедшие
  const riders = useMemo(() => {
    const list = ((data && data.riders) || []).slice();

    return list.sort((a, b) => {
      if (a.position && b.position) return a.position - b.position;
      if (a.position) return -1;
      if (b.position) return 1;

      return (a.finish_time || Infinity) - (b.finish_time || Infinity);
    });
  }, [data]);

  // Цвет достается только тем, кто показан на графике: участников на этапе
  // может быть сотня, а различимых цветов — десяток
  const colorById = useMemo(() => {
    const map = new Map();
    let index = 0;

    riders.forEach(rider => {
      if (!shownIds.has(rider.user_id)) return;

      map.set(rider.user_id, RIDER_COLORS[index % RIDER_COLORS.length]);
      index += 1;
    });

    return map;
  }, [riders, shownIds]);

  // Кого показываем на графике отрывов сразу после загрузки: призеров,
  // а также текущего пользователя, если он проехал этап
  useEffect(() => {
    if (riders.length === 0) return;

    setShownIds(prev => {
      if (prev.size > 0) return prev;

      const next = new Set(riders.slice(0, DEFAULT_RIDERS).map(rider => rider.user_id));
      if (riders.some(rider => rider.user_id === currentUserId)) next.add(currentUserId);

      return next;
    });

    setReferenceId(prev => (
      riders.some(rider => rider.user_id === prev) ? prev : (data.leader_id || riders[0].user_id)
    ));
  }, [riders, currentUserId, data]);

  const profile = useMemo(() => smoothProfile((data && data.profile) || []), [data]);

  const riderName = (rider) => (
    language !== 'ru' && rider.first_name_eng && rider.last_name_eng
      ? `${rider.first_name_eng} ${rider.last_name_eng}`
      : `${rider.first_name} ${rider.last_name}`
  );

  if (!isOpen) return null;

  const analyzerRouvyId = data && data.route && data.route.rouvy_id;
  const totalDistance = segments.length ? segments[segments.length - 1].end : 0;
  const segment = segments[segmentIndex] || null;
  const reference = riders.find(rider => rider.user_id === referenceId) || null;

  // Геометрия графиков — общая для профиля и отрывов
  const width = isMobile ? 380 : 720;
  const margin = isMobile
    ? { top: 16, right: 14, bottom: 40, left: 44 }
    : { top: 20, right: 24, bottom: 44, left: 56 };
  const plotWidth = width - margin.left - margin.right;

  const xFor = (distance) => (
    totalDistance > 0 ? margin.left + (plotWidth * distance) / totalDistance : margin.left
  );

  const distanceTicks = () => {
    const step = niceStep((totalDistance / 1000) / (isMobile ? 4 : 7));
    const ticks = [];

    for (let km = 0; km * 1000 <= totalDistance; km += step) {
      ticks.push(Math.round(km * 100) / 100);
    }

    return ticks;
  };

  // Профиль высот: путь по точкам и замыкание к низу поля графика
  const profileArea = (plotTop, plotHeight, share) => {
    if (profile.length < 2) return null;

    const altitudes = profile.map(point => point[1]);
    const min = Math.min(...altitudes);
    const span = Math.max(1, Math.max(...altitudes) - min);
    const bottom = plotTop + plotHeight;
    const yFor = (altitude) => bottom - (plotHeight * share * (altitude - min)) / span;

    const line = profile
      .map(([distance, altitude], index) => `${index === 0 ? 'M' : 'L'} ${xFor(distance)} ${yFor(altitude)}`)
      .join(' ');

    return `${line} L ${xFor(profile[profile.length - 1][0])} ${bottom} L ${xFor(profile[0][0])} ${bottom} Z`;
  };

  // ── Вкладка «Участки» ──────────────────────────────────────────────────────

  const segmentTitle = (item) => {
    const number = segments.filter(s => s.kind === item.kind && s.index <= item.index).length;

    return `${t(KIND_LABEL_KEYS[item.kind])} ${number}`;
  };

  // Подсказка на подложке профиля: тип участка, длина, уклон и категория
  const segmentTooltip = (item) => {
    const parts = [
      segmentTitle(item),
      `${(item.length / 1000).toFixed(1)} ${t('results.compare_km')}`,
      `${item.avg_gradient}%`
    ];

    if (item.kind === 'climb') parts.push(`+${Math.round(item.gain)} ${t('results.replay_meters')}`);
    if (item.category) parts.push(`${t('results.analysis_category')} ${item.category}`);

    return parts.join(' · ');
  };

  // Подъемы маршрута и лидер на каждом из них: сплиты уже посчитаны бэкендом,
  // лучшее время на участке — это rank 1
  const climbs = segments.filter(item => item.kind === 'climb');

  const climbLeader = (item) => {
    let best = null;

    riders.forEach(rider => {
      const split = rider.segments[item.index];
      if (!split || split.duration === null || split.duration === undefined) return;
      if (best === null || split.duration < best.split.duration) best = { rider, split };
    });

    return best;
  };

  // Легенда категорий: показываем только те, что есть на маршруте, плюс
  // подъемы без категории — иначе легенда шире самого профиля
  const renderCategoryLegend = () => {
    const present = CATEGORIES.filter(category => climbs.some(item => item.category === category));
    const hasPlain = climbs.some(item => !item.category);

    if (present.length === 0 && !hasPlain) return null;

    return (
      <div className="race-analysis-legend">
        <span className="race-analysis-legend-title" title={t('results.analysis_category_hint')}>
          {t('results.analysis_category_legend')}
        </span>
        {present.map(category => (
          <span key={`legend-${category}`} className="race-analysis-legend-item">
            <span className={`race-analysis-legend-swatch ${categoryClass(category)}`} />
            {category === 'HC' ? t('results.analysis_category_hc') : `${t('results.analysis_category')} ${category}`}
          </span>
        ))}
        {hasPlain && (
          <span className="race-analysis-legend-item">
            <span className={`race-analysis-legend-swatch ${categoryClass(null)}`} />
            {t('results.analysis_category_none')}
          </span>
        )}
      </div>
    );
  };

  const renderProfileChart = () => {
    const height = isMobile ? 170 : 210;
    const plotHeight = height - margin.top - margin.bottom;
    const path = profileArea(margin.top, plotHeight, 1);

    return (
      <svg
        className="race-analysis-profile-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={t('results.analysis_profile')}
      >
        {/* Рельеф красится не полосой во всю высоту, а самой заливкой под
            кривой — как на профилях этапов: тип участка и категория подъема
            видны прямо в силуэте горы */}
        <defs>
          {segments.map(item => (
            <clipPath key={`clip-${item.index}`} id={`race-analysis-clip-${item.index}`}>
              <rect
                x={xFor(item.start)}
                y={margin.top}
                width={Math.max(1, xFor(item.end) - xFor(item.start))}
                height={plotHeight}
              />
            </clipPath>
          ))}
        </defs>

        {path && segments.map(item => (
          <path
            key={`relief-${item.index}`}
            className={`race-analysis-relief race-analysis-relief-${item.kind}${item.kind === 'climb' ? ` ${categoryClass(item.category)}` : ''}`}
            d={path}
            clipPath={`url(#race-analysis-clip-${item.index})`}
          />
        ))}

        {path && <path className="race-analysis-profile-path" d={path} />}

        {/* Прозрачные прямоугольники поверх — клик и подсказка по участку */}
        {segments.map(item => (
          <rect
            key={`band-${item.index}`}
            className="race-analysis-band"
            x={xFor(item.start)}
            y={margin.top}
            width={Math.max(1, xFor(item.end) - xFor(item.start))}
            height={plotHeight}
            onClick={() => setSegmentIndex(item.index)}
          >
            <title>{segmentTooltip(item)}</title>
          </rect>
        ))}

        {/* Рамка выбранного участка нужна только там, где участок выбирают */}
        {segment && tab === 'segments' && (
          <rect
            className="race-analysis-band-active"
            x={xFor(segment.start)}
            y={margin.top}
            width={Math.max(1, xFor(segment.end) - xFor(segment.start))}
            height={plotHeight}
          />
        )}

        {/* Номера участков над подложкой */}
        {segments.map(item => (
          <text
            key={`label-${item.index}`}
            className="race-analysis-band-label"
            x={(xFor(item.start) + xFor(item.end)) / 2}
            y={margin.top + 12}
            textAnchor="middle"
          >
            {item.index + 1}
          </text>
        ))}

        {distanceTicks().map(km => (
          <text
            key={`x-${km}`}
            className="race-analysis-axis-label"
            x={xFor(km * 1000)}
            y={height - margin.bottom + 16}
            textAnchor="middle"
          >
            {km}
          </text>
        ))}

        <text
          className="race-analysis-axis-title"
          x={margin.left + plotWidth / 2}
          y={height - 8}
          textAnchor="middle"
        >
          {t('results.compare_axis_distance')}
        </text>
      </svg>
    );
  };

  const renderSegmentPicker = () => (
    <div className="race-analysis-segments">
      {segments.map(item => (
        <button
          key={`pick-${item.index}`}
          type="button"
          className={`race-analysis-segment race-analysis-segment-${item.kind}${item.category ? ` ${categoryClass(item.category)}` : ''}${item.index === segmentIndex ? ' race-analysis-segment-active' : ''}`}
          onClick={() => setSegmentIndex(item.index)}
        >
          <span className="race-analysis-segment-title">{segmentTitle(item)}</span>
          <span className="race-analysis-segment-stats">
            {(item.length / 1000).toFixed(1)} {t('results.compare_km')} · {item.avg_gradient}%
            {item.kind === 'climb' && ` · +${Math.round(item.gain)} ${t('results.replay_meters')}`}
          </span>
          {item.category && (
            <span
              className={`race-analysis-category ${categoryClass(item.category)}`}
              title={t('results.analysis_category_hint')}
            >
              {item.category === 'HC'
                ? t('results.analysis_category_hc')
                : `${t('results.analysis_category')} ${item.category}`}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const renderSplitsTable = () => {
    if (!segment) return null;

    const rows = riders
      .map(rider => ({ rider, split: rider.segments[segment.index] || {} }))
      .sort((a, b) => {
        if (a.split.duration === null || a.split.duration === undefined) return 1;
        if (b.split.duration === null || b.split.duration === undefined) return -1;

        return a.split.duration - b.split.duration;
      });

    return (
      <div className="race-analysis-table-wrap">
        <table className="race-analysis-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('results.analysis_rider')}</th>
              <th>{t('results.analysis_time')}</th>
              <th>{t('results.analysis_gap')}</th>
              <th>{t('results.replay_power')}</th>
              <th>{t('results.replay_speed')}</th>
              {segment.kind === 'climb' && <th>{t('results.analysis_vam')}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ rider, split }) => (
              <tr
                key={`row-${rider.user_id}`}
                className={rider.user_id === currentUserId ? 'race-analysis-row-you' : ''}
              >
                <td>{split.rank || '—'}</td>
                <td className="race-analysis-cell-name">
                  {/* Цветная точка связывает строку с линией на графике отрывов,
                      у остальных участников она нейтральная */}
                  <span
                    className={`race-analysis-dot${colorById.has(rider.user_id) ? '' : ' race-analysis-dot-muted'}`}
                    style={colorById.has(rider.user_id) ? { background: colorById.get(rider.user_id) } : undefined}
                  />
                  {riderName(rider)}
                </td>
                <td>{formatDuration(split.duration)}</td>
                <td className="race-analysis-cell-gap">{split.rank === 1 ? '—' : formatGap(split.gap)}</td>
                <td>{split.avg_power ? `${split.avg_power} ${t('results.compare_axis_power_unit')}` : '—'}</td>
                <td>{split.avg_speed ? `${(split.avg_speed * 3.6).toFixed(1)} ${t('results.analysis_kmh')}` : '—'}</td>
                {segment.kind === 'climb' && <td>{split.vam || '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Вкладка «Подъемы» ──────────────────────────────────────────────────────

  // Горная классификация этапа: по каждому подъему — кто прошел его быстрее
  // всех. Клик по строке открывает полную таблицу сплитов на этом подъеме
  const renderClimbs = () => {
    if (climbs.length === 0) {
      return <div className="race-analysis-state">{t('results.analysis_no_climbs')}</div>;
    }

    return (
      <div className="race-analysis-table-wrap">
        <table className="race-analysis-table race-analysis-climbs-table">
          <thead>
            <tr>
              <th>{t('results.analysis_climb')}</th>
              <th>{t('results.analysis_climb_profile')}</th>
              <th>{t('results.analysis_climb_leader')}</th>
              <th>{t('results.analysis_time')}</th>
              <th>{t('results.analysis_vam')}</th>
            </tr>
          </thead>
          <tbody>
            {climbs.map(item => {
              const best = climbLeader(item);

              return (
                <tr
                  key={`climb-${item.index}`}
                  className={`race-analysis-climb-row${best && best.rider.user_id === currentUserId ? ' race-analysis-row-you' : ''}`}
                  onClick={() => {
                    setSegmentIndex(item.index);
                    setTab('segments');
                  }}
                >
                  <td>
                    <span className="race-analysis-climb-name">{segmentTitle(item)}</span>
                    <span className="race-analysis-climb-where">
                      {(item.start / 1000).toFixed(1)}–{(item.end / 1000).toFixed(1)} {t('results.compare_km')}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`race-analysis-category ${categoryClass(item.category)}`}
                      title={t('results.analysis_category_hint')}
                    >
                      {item.category
                        ? (item.category === 'HC'
                          ? t('results.analysis_category_hc')
                          : `${t('results.analysis_category')} ${item.category}`)
                        : t('results.analysis_category_none')}
                    </span>
                    <span className="race-analysis-climb-where">
                      {(item.length / 1000).toFixed(1)} {t('results.compare_km')} · +{Math.round(item.gain)} {t('results.replay_meters')}
                      {' · '}{item.avg_gradient}% ({t('results.analysis_max_gradient')} {item.max_gradient}%)
                    </span>
                  </td>
                  <td className="race-analysis-climb-leader">
                    {best ? riderName(best.rider) : '—'}
                    {best && best.rider.team_name && (
                      <span className="race-analysis-climb-team">{best.rider.team_name}</span>
                    )}
                  </td>
                  <td>{best ? formatDuration(best.split.duration) : '—'}</td>
                  <td>{best && best.split.vam ? best.split.vam : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Вкладка «Отрывы» ───────────────────────────────────────────────────────

  // Отставание от эталона в каждой контрольной точке: null там, куда
  // кто-то из двоих не доехал — линия просто прерывается
  const gapsFor = (rider) => {
    if (!reference) return [];

    return checkpoints.map((_, index) => {
      const own = rider.times[index];
      const base = reference.times[index];

      if (own === null || own === undefined || base === null || base === undefined) return null;

      return own - base;
    });
  };

  const gapSeries = riders
    .filter(rider => shownIds.has(rider.user_id))
    .map(rider => ({ rider, color: colorById.get(rider.user_id), gaps: gapsFor(rider) }));

  const renderGapChart = () => {
    const height = isMobile ? 300 : 360;
    const plotHeight = height - margin.top - margin.bottom;
    const backdrop = profileArea(margin.top, plotHeight, 0.4);

    const values = gapSeries.flatMap(({ gaps }) => gaps.filter(gap => gap !== null));
    const maxGap = values.length ? Math.max(...values, 0) : 60;
    const minGap = values.length ? Math.min(...values, 0) : 0;
    const span = Math.max(10, maxGap - minGap);
    // Отставание растет вниз: эталон идет по верхней части поля
    const yFor = (gap) => margin.top + (plotHeight * (gap - minGap + span * 0.05)) / (span * 1.1);

    const step = niceStep(span / 5);
    const ticks = [];
    for (let value = Math.ceil(minGap / step) * step; value <= maxGap + step / 2; value += step) {
      ticks.push(Math.round(value));
    }

    // Линия рвется там, где данных нет
    const pathFor = (gaps) => {
      let path = '';
      let open = false;

      gaps.forEach((gap, index) => {
        if (gap === null) {
          open = false;
          return;
        }

        path += `${open ? 'L' : 'M'} ${xFor(checkpoints[index])} ${yFor(gap)} `;
        open = true;
      });

      return path.trim();
    };

    const distanceAt = (clientX) => {
      const svg = gapSvgRef.current;
      if (!svg || totalDistance <= 0) return null;

      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return null;

      const x = ((clientX - rect.left) / rect.width) * width;
      const ratio = Math.min(1, Math.max(0, (x - margin.left) / plotWidth));

      return ratio * totalDistance;
    };

    return (
      <svg
        ref={gapSvgRef}
        className="race-analysis-gap-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={t('results.analysis_tab_gaps')}
        onMouseMove={(e) => setCursorDistance(distanceAt(e.clientX))}
        onMouseLeave={() => setCursorDistance(null)}
        onTouchMove={(e) => setCursorDistance(distanceAt(e.touches[0].clientX))}
        onTouchEnd={() => setCursorDistance(null)}
      >
        {/* Рельеф — подложка: видно, на каком подъеме группа разъехалась */}
        {backdrop && <path className="race-analysis-gap-profile" d={backdrop} />}

        {ticks.map(tick => (
          <g key={`gap-y-${tick}`}>
            <line
              className={`race-analysis-grid${tick === 0 ? ' race-analysis-grid-zero' : ''}`}
              x1={margin.left}
              x2={width - margin.right}
              y1={yFor(tick)}
              y2={yFor(tick)}
            />
            <text
              className="race-analysis-axis-label"
              x={margin.left - 8}
              y={yFor(tick) + 4}
              textAnchor="end"
            >
              {tick}
            </text>
          </g>
        ))}

        {distanceTicks().map(km => (
          <text
            key={`gap-x-${km}`}
            className="race-analysis-axis-label"
            x={xFor(km * 1000)}
            y={height - margin.bottom + 16}
            textAnchor="middle"
          >
            {km}
          </text>
        ))}

        {/* Границы участков маршрута: сразу видно, где начался подъем */}
        {segments.filter(item => item.index > 0).map(item => (
          <line
            key={`gap-seg-${item.index}`}
            className="race-analysis-segment-divider"
            x1={xFor(item.start)}
            x2={xFor(item.start)}
            y1={margin.top}
            y2={margin.top + plotHeight}
          />
        ))}

        {gapSeries.map(({ rider, color, gaps }) => (
          <path
            key={`gap-line-${rider.user_id}`}
            className="race-analysis-gap-line"
            d={pathFor(gaps)}
            stroke={color}
          />
        ))}

        {cursorDistance !== null && (
          <line
            className="race-analysis-cursor"
            x1={xFor(cursorDistance)}
            x2={xFor(cursorDistance)}
            y1={margin.top}
            y2={margin.top + plotHeight}
          />
        )}

        <text
          className="race-analysis-axis-title"
          x={margin.left + plotWidth / 2}
          y={height - 8}
          textAnchor="middle"
        >
          {t('results.compare_axis_distance')}
        </text>
        <text
          className="race-analysis-axis-title"
          x={-(margin.top + plotHeight / 2)}
          y={12}
          transform="rotate(-90)"
          textAnchor="middle"
        >
          {t('results.analysis_axis_gap')}
        </text>
      </svg>
    );
  };

  // Подсказка и значения под курсором лежат в одной ячейке грида и обе всегда
  // отрисованы: скрытая продолжает занимать место. Иначе появление значений
  // на наведении меняло бы высоту окна, а вместе с ней прыгал бы и график.
  // Высота блока со значениями зависит только от числа выбранных участников,
  // поэтому при движении курсора она уже не меняется
  const renderGapFootnote = () => {
    const showValues = cursorDistance !== null && checkpoints.length > 0;
    const index = showValues
      ? Math.min(
        checkpoints.length - 1,
        Math.max(0, Math.round((cursorDistance / totalDistance) * (checkpoints.length - 1)))
      )
      : 0;

    return (
      <div className="race-analysis-footnote">
        <div className={`race-analysis-hint${showValues ? ' race-analysis-footnote-hidden' : ''}`}>
          {t('results.analysis_gaps_hint')}
        </div>
        <div
          className={`race-analysis-readout${showValues ? '' : ' race-analysis-footnote-hidden'}`}
          aria-hidden={!showValues}
        >
          <span className="race-analysis-readout-distance">
            {((showValues ? checkpoints[index] : 0) / 1000).toFixed(1)} {t('results.compare_km')}
          </span>
          {gapSeries.map(({ rider, color, gaps }) => (
            <span key={`readout-${rider.user_id}`} className="race-analysis-readout-value">
              <span className="race-analysis-dot" style={{ background: color }} />
              {riderName(rider)}:
              <strong>{!showValues || gaps[index] === null ? '—' : formatGap(gaps[index])}</strong>
            </span>
          ))}
        </div>
      </div>
    );
  };

  const toggleRider = (userId) => {
    setShownIds(prev => {
      const next = new Set(prev);

      if (next.has(userId)) {
        next.delete(userId);
      } else if (next.size < MAX_SHOWN_RIDERS) {
        next.add(userId);
      }

      return next;
    });
  };

  const showRiders = (list) => setShownIds(new Set(list.slice(0, MAX_SHOWN_RIDERS).map(rider => rider.user_id)));

  const myTeam = (riders.find(rider => rider.user_id === currentUserId) || {}).team_name;

  const label = (key, values) => Object.entries(values).reduce(
    (text, [name, value]) => text.replace(`{${name}}`, value),
    t(key)
  );

  const renderRiderPicker = () => {
    const query = riderQuery.trim().toLowerCase();
    const found = query
      ? riders.filter(rider => (
        riderName(rider).toLowerCase().includes(query)
          || (rider.nickname || '').toLowerCase().includes(query)
          || (rider.team_name || '').toLowerCase().includes(query)
      ))
      : riders;
    const atLimit = shownIds.size >= MAX_SHOWN_RIDERS;

    return (
      <div className="race-analysis-picker">
        <div className="race-analysis-picker-head">
          <input
            type="text"
            className="race-analysis-search"
            value={riderQuery}
            placeholder={t('results.analysis_search')}
            onChange={(e) => setRiderQuery(e.target.value)}
          />

          <div className="race-analysis-presets">
            <button type="button" onClick={() => showRiders(riders)}>
              {label('results.analysis_preset_top', { count: MAX_SHOWN_RIDERS })}
            </button>
            {myTeam && (
              <button
                type="button"
                onClick={() => showRiders(riders.filter(rider => rider.team_name === myTeam))}
              >
                {t('results.analysis_preset_team')}
              </button>
            )}
            <button type="button" onClick={() => setShownIds(new Set())}>
              {t('results.analysis_preset_clear')}
            </button>
          </div>
        </div>

        <div className="race-analysis-rider-list">
          {found.length === 0 && (
            <div className="race-analysis-rider-empty">{t('results.analysis_not_found')}</div>
          )}

          {found.map(rider => {
            const active = shownIds.has(rider.user_id);

            return (
              <label
                key={`pick-rider-${rider.user_id}`}
                className={`race-analysis-rider-row${active ? ' race-analysis-rider-row-active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  disabled={!active && atLimit}
                  onChange={() => toggleRider(rider.user_id)}
                />
                <span
                  className={`race-analysis-dot${active ? '' : ' race-analysis-dot-muted'}`}
                  style={active ? { background: colorById.get(rider.user_id) } : undefined}
                />
                <span className="race-analysis-rider-row-name">
                  {rider.position ? `${rider.position}. ` : ''}{riderName(rider)}
                  {rider.user_id === currentUserId && (
                    <span className="race-analysis-you">{t('results.compare_you')}</span>
                  )}
                </span>
                {rider.team_name && (
                  <span className="race-analysis-rider-row-team">{rider.team_name}</span>
                )}
                {rider.start_power && (
                  <span className="race-analysis-start-power">
                    {t('results.analysis_start_power')}: {rider.start_power}/{rider.avg_power || '—'}
                  </span>
                )}
              </label>
            );
          })}
        </div>

        <div className="race-analysis-picker-counter">
          {label('results.analysis_selected_count', { count: shownIds.size, max: MAX_SHOWN_RIDERS })}
          {atLimit && ` · ${t('results.analysis_limit_hint')}`}
        </div>
      </div>
    );
  };

  const renderGapControls = () => (
    <div className="race-analysis-gap-controls">
      {/* Эталон выбирается поиском: в нативном списке на сотню участников
          нужного пришлось бы искать глазами */}
      <div className="race-analysis-reference">
        <span className="race-analysis-reference-label">{t('results.analysis_reference')}</span>
        <div className="race-analysis-reference-select">
          <EventSearchSelect
            options={riders.map(rider => ({
              id: rider.user_id,
              name: `${rider.position ? `${rider.position}. ` : ''}${riderName(rider)}`
            }))}
            value={referenceId}
            onChange={(id) => setReferenceId(Number(id))}
            placeholder={t('results.analysis_reference')}
            emptyText={t('results.analysis_not_found')}
          />
        </div>
      </div>

      {renderRiderPicker()}
    </div>
  );

  // ── Каркас окна ────────────────────────────────────────────────────────────

  const renderBody = () => {
    if (loading) return <div className="race-analysis-state">{t('common.loading')}</div>;

    if (error) {
      return (
        <div className="race-analysis-state">
          <div>{error}</div>
          <button className="race-analysis-retry" onClick={() => setReloadToken(prev => prev + 1)}>
            {t('results.replay_retry')}
          </button>
        </div>
      );
    }

    if (segments.length === 0 || riders.length === 0) {
      return <div className="race-analysis-state">{t('results.analysis_no_data')}</div>;
    }

    return (
      <>
        <div className="race-analysis-tabs">
          <button
            type="button"
            className={`race-analysis-tab${tab === 'segments' ? ' race-analysis-tab-active' : ''}`}
            onClick={() => setTab('segments')}
          >
            {t('results.analysis_tab_segments')}
          </button>
          <button
            type="button"
            className={`race-analysis-tab${tab === 'climbs' ? ' race-analysis-tab-active' : ''}`}
            onClick={() => setTab('climbs')}
          >
            {t('results.analysis_tab_climbs')}
          </button>
          <button
            type="button"
            className={`race-analysis-tab${tab === 'gaps' ? ' race-analysis-tab-active' : ''}`}
            onClick={() => setTab('gaps')}
          >
            {t('results.analysis_tab_gaps')}
          </button>
        </div>

        {tab === 'segments' && (
          <>
            {renderProfileChart()}
            {renderCategoryLegend()}
            {renderSegmentPicker()}
            <div className="race-analysis-hint">{t('results.analysis_segments_hint')}</div>
            {renderSplitsTable()}
          </>
        )}

        {tab === 'climbs' && (
          <>
            {renderProfileChart()}
            {renderCategoryLegend()}
            <div className="race-analysis-hint">{t('results.analysis_climbs_hint')}</div>
            {renderClimbs()}
          </>
        )}

        {tab === 'gaps' && (
          <>
            {renderGapControls()}
            {renderGapChart()}
            {renderGapFootnote()}
          </>
        )}
      </>
    );
  };

  return (
    // Окно открывается со страницы результатов поверх остального содержимого
    <div
      className="race-analysis-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="race-analysis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="race-analysis-header">
          <div>
            <h3 className="race-analysis-title">{t('results.analysis_title')}</h3>
            <div className="race-analysis-subtitle">
              {routeName}
              {/* Наш разбор смотрит назад, на уже проеханную гонку. Прогноз по
                  этому же маршруту вперед дает сторонний Rouvy Analyzer */}
              {analyzerRouvyId && (
                <button
                  type="button"
                  className="race-analysis-analyzer-link"
                  onClick={() => setIsAnalyzerOpen(true)}
                  title={t('results.analysis_analyzer_hint')}
                >
                  {t('results.analysis_analyzer')}
                </button>
              )}
            </div>
          </div>
          <button className="race-analysis-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="race-analysis-body">{renderBody()}</div>
      </div>

      <RouvyAnalyzerModal
        isOpen={isAnalyzerOpen}
        onClose={() => setIsAnalyzerOpen(false)}
        rouvyId={analyzerRouvyId}
        routeName={routeName}
      />
    </div>
  );
};

export default RaceAnalysisModal;
